import {
  activationRefusal,
  roleChangeRefusal,
  type Profile,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { supabase, unwrap } from "../../lib/supabase";
import { useSession } from "../../session";

/**
 * Users — who can sign in, and what they may reach.
 *
 * Creating an auth user, stamping role claims and banning a login all need the
 * service_role key, which must never be in the browser. They go through the
 * `manage-users` Edge Function instead, which verifies the caller is an admin
 * before doing anything.
 *
 * Agents are users too: an agent gets its own row here with the `agent` role
 * plus whichever agent_* role decides which MCP tools it may call.
 *
 * `super_admin` holds every other role (ADR 0152). Only a super admin can grant
 * or remove it, so for anybody else its chip is shown but cannot be pressed —
 * the Edge Function refuses the change either way.
 */

const ROLE_OPTIONS: { value: string; label: string; hint: string }[] = [
  {
    value: "super_admin",
    label: "Super admin",
    hint: "Holds every role, including any added later. Only a super admin can grant it (ADR 0152).",
  },
  { value: "admin", label: "Admin", hint: "Full access to everything." },
  {
    value: "tester",
    label: "Tester",
    hint: "Sees Test jobs and can mark one. Everyone without it never sees them (ADR 0151).",
  },
  {
    value: "agent",
    label: "Agent (data access)",
    hint: "Lets an AI agent read and write through the MCP server.",
  },
  {
    value: "agent_analyst",
    label: "Agent · read-only",
    hint: "MCP: queries and reports only.",
  },
  {
    value: "agent_prospector",
    label: "Agent · prospector",
    hint: "MCP: reads, plus creating prospects — new customers and jobs only.",
  },
  {
    value: "agent_coordinator",
    label: "Agent · coordinator",
    hint: "MCP: reads, notes and calls, checklist steps, inspection and work dates. No production handoff.",
  },
  {
    value: "agent_comms",
    label: "Agent · comms",
    hint: "MCP: notes, checklists, scheduling, new leads.",
  },
  {
    value: "agent_estimator",
    label: "Agent · estimator",
    hint: "MCP: estimates, orders and expenses — money-mutating.",
  },
];

interface AuthUserSummary {
  id: string;
  email: string | null;
  roles: string[];
  banned: boolean;
  last_sign_in_at: string | null;
}

export function UsersTool() {
  const { isAdmin, session, roles: callerRoles } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const query = useAsync(async () => {
    const [profiles, users] = await Promise.all([
      supabase
        .from("profiles")
        .select("*")
        .order("sort_order")
        .order("full_name"),
      callManageUsers<{ users: AuthUserSummary[] }>("list", {}),
    ]);
    return {
      profiles: unwrap<Profile[]>(profiles),
      users: users?.users ?? [],
    };
  }, []);

  async function callManageUsers<T>(
    action: string,
    payload: Record<string, unknown>,
  ): Promise<T | null> {
    const { data, error: fnError } = await supabase.functions.invoke(
      "manage-users",
      { body: { action, ...payload } },
    );
    if (fnError) {
      // A refusal comes back as a non-2xx with the reason in the body; say
      // that, not "deploy the function", which is only right when it is not
      // there at all.
      const reason = await refusalReason(fnError);
      setError(
        reason ??
          t(
            "User management is unavailable: {message}. Deploy the manage-users Edge Function (see supabase/functions/manage-users).",
            { message: fnError.message },
          ),
      );
      return null;
    }
    return data as T;
  }

  if (!isAdmin) {
    return (
      <div className="shelf">
        <b>{t("Admins only")}</b>
        {t("Ask an admin to change who can sign in.")}
      </div>
    );
  }

  const profiles = query.data?.profiles ?? [];
  const users = query.data?.users ?? [];
  const userByEmail = new Map(
    users.map((u) => [(u.email ?? "").toLowerCase(), u]),
  );

  const guard = async (action: () => Promise<void>) => {
    try {
      await action();
      await query.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <ErrorNote error={error} />

      <div className="toolbar2">
        <span className="count">
          {tn(profiles.length, "1 person", "{n} people")}
        </span>
        <button
          className="addrow"
          style={{ marginTop: 0 }}
          onClick={() => setInviting(true)}
        >
          {t("+ Invite user")}
        </button>
      </div>

      <div className="ewrap">
        <table className="etable">
          <thead>
            <tr>
              <th>{t("Name")}</th>
              <th>{t("Email")}</th>
              <th>{t("Sign-in")}</th>
              <th>{t("Roles")}</th>
              <th>{t("Last sign-in")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile) => {
              const user = userByEmail.get((profile.email ?? "").toLowerCase());
              const isMe = user?.id === session.user.id;
              return (
                <tr key={profile.id}>
                  <td>{profile.full_name ?? "—"}</td>
                  <td>{profile.email ?? "—"}</td>
                  <td>
                    {!user ? (
                      <span className="na-hint">{t("no login")}</span>
                    ) : user.banned ? (
                      <span className="badge draft">{t("deactivated")}</span>
                    ) : (
                      <span className="badge approved">{t("active")}</span>
                    )}
                  </td>
                  <td>
                    {user ? (
                      <RolePicker
                        roles={user.roles}
                        callerRoles={callerRoles}
                        disabled={isMe}
                        onChange={(roles) =>
                          void guard(async () => {
                            await callManageUsers("set-roles", {
                              userId: user.id,
                              roles,
                            });
                          })
                        }
                      />
                    ) : (
                      <span className="na-hint">—</span>
                    )}
                  </td>
                  <td className="sub">
                    {user?.last_sign_in_at
                      ? new Date(user.last_sign_in_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td>
                    {user &&
                      activationRefusal({
                        callerRoles,
                        isSelf: isMe,
                        targetRoles: user.roles,
                      }) === null && (
                        <button
                          className="addrow ghost"
                          style={{ marginTop: 0 }}
                          onClick={() => {
                            const next = !user.banned;
                            if (
                              next &&
                              !window.confirm(
                                t(
                                  "Deactivate {name}? They are signed out immediately and cannot sign back in.",
                                  { name: String(profile.full_name) },
                                ),
                              )
                            )
                              return;
                            void guard(async () => {
                              await callManageUsers("set-active", {
                                userId: user.id,
                                active: !next,
                              });
                            });
                          }}
                        >
                          {user.banned ? t("Reactivate") : t("Deactivate")}
                        </button>
                      )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="na-hint" style={{ marginTop: 10 }}>
        {t(
          "Roles are carried in the sign-in token and decide what the database will return. A change takes effect the next time that person's session refreshes; deactivating takes effect immediately.",
        )}
      </div>

      {inviting && (
        <InviteModal
          callerRoles={callerRoles}
          onClose={() => setInviting(false)}
          onInvite={async (email, fullName, roles) => {
            await guard(async () => {
              await callManageUsers("invite", { email, fullName, roles });
            });
            setInviting(false);
          }}
        />
      )}
    </>
  );
}

/**
 * The reason the manage-users function gave for refusing, if it gave one.
 *
 * supabase-js reports any non-2xx as a FunctionsHttpError whose `context` is
 * the raw Response; the function always answers `{ error }`. Null when there is
 * no such body — the function is missing, or the network failed.
 *
 * Exported for `role-picker.test.tsx`.
 */
export async function refusalReason(fnError: unknown): Promise<string | null> {
  const context = (fnError as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;
  try {
    const body = (await context.clone().json()) as { error?: unknown };
    return typeof body.error === "string" && body.error.trim() !== ""
      ? body.error
      : null;
  } catch {
    return null;
  }
}

/**
 * The chips that decide what a login may reach.
 *
 * Exported for `role-picker.test.tsx`, the same way `NewPartner` exports its
 * consent block: this is where a role is granted, and since the database began
 * checking the money tier a mistake here is a permission mistake rather than a
 * cosmetic one.
 */
export function RolePicker({
  roles,
  callerRoles,
  onChange,
  disabled,
}: {
  roles: string[];
  /** The signed-in person's roles, which decide which chips they may press. */
  callerRoles: readonly string[];
  onChange: (roles: string[]) => void;
  disabled?: boolean;
}) {
  return (
    <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {ROLE_OPTIONS.map((option) => {
        const on = roles.includes(option.value);
        const next = on
          ? roles.filter((r) => r !== option.value)
          : [...roles, option.value];
        const refusal = roleChangeRefusal({
          callerRoles,
          isSelf: false,
          before: roles,
          after: next,
        });
        return (
          <button
            key={option.value}
            className={`rolechip${on ? " on" : ""}`}
            title={refusal ? t(refusal) : t(option.hint)}
            disabled={disabled || refusal !== null}
            onClick={() => onChange(next)}
          >
            {t(option.label)}
          </button>
        );
      })}
    </span>
  );
}

function InviteModal({
  callerRoles,
  onClose,
  onInvite,
}: {
  callerRoles: readonly string[];
  onClose: () => void;
  onInvite: (email: string, fullName: string, roles: string[]) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState<string[]>(["admin"]);
  const [busy, setBusy] = useState(false);

  return (
    <Modal
      title={t("Invite user")}
      subtitle={t(
        "Sends a Supabase invite email. They set their own password — nobody here ever sees it.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button
            className="btn-primary"
            disabled={busy || !email.trim()}
            onClick={() => {
              setBusy(true);
              void onInvite(email.trim(), fullName.trim(), roles).finally(() =>
                setBusy(false),
              );
            }}
          >
            {t("Send invite")}
          </button>
        </>
      }
    >
      <label className="na-hint">{t("Email")}</label>
      <input value={email} onChange={(e) => setEmail(e.target.value)} />
      <label className="na-hint">{t("Full name")}</label>
      <input value={fullName} onChange={(e) => setFullName(e.target.value)} />
      <label className="na-hint">{t("Roles")}</label>
      <RolePicker roles={roles} callerRoles={callerRoles} onChange={setRoles} />
    </Modal>
  );
}
